import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { HelpHubDTO } from "@/types/help-hub/help-hub";
import type { HelpHubStageState, HubChannel } from "@/types/help-hub/presentation";

/** Shared page-only interactions; public data and live conversations retain their existing paths. */
export const useHelpHubPresentation = (hub: HelpHubDTO | null | undefined, scope: string) => {
  const [stageState, setStageState] = useState<HelpHubStageState>(null);
  const trigger = useRef<HTMLElement | null>(null);
  // A route change must never carry an open conversation into another Genie/guide.
  const stage = stageState?.scope === scope ? stageState : null;
  useEffect(() => { setStageState(null); }, [scope]);

  useEffect(() => {
    const widgets = [document.getElementById("helpgenie-widget-container"), document.querySelector<HTMLElement>(".hgv2-host")];
    const previous = widgets.map(widget => widget?.style.display);
    widgets.forEach(widget => { if (widget) widget.style.display = "none"; });
    return () => widgets.forEach((widget, index) => { if (widget) widget.style.display = previous[index] ?? ""; });
  }, []);

  const openWithQuestion = useCallback((value: string) => {
    const question = value.trim();
    if (!question) return;
    trigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setStageState({ scope, question });
  }, [scope]);

  const openStage = useCallback(() => {
    trigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setStageState({ scope });
  }, [scope]);

  const closeStage = useCallback(() => {
    setStageState(null);
    requestAnimationFrame(() => { if (trigger.current?.isConnected) trigger.current.focus({ preventScroll: true }); });
  }, []);

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  }, []);

  const channels = useMemo<HubChannel[]>(() => {
    if (!hub) return [];
    const list: HubChannel[] = [];
    // The Genie's own line leads; a page without one falls back to the first number the owner added.
    const callNumber = hub.phoneNumber ?? hub.extraPhones[0]?.number;
    if (callNumber) list.push({ key: "call", label: "Call us", detail: callNumber, genieIcon: "phone", href: `tel:${callNumber}` });
    if (hub.topics.some(topic => topic.guides.length > 0)) list.push({ key: "guides", label: "Read the guides", detail: `${hub.guideCount} published`, genieIcon: "book-open", onClick: () => scrollTo("guides") });
    if (hub.downloads.length > 0) list.push({ key: "downloads", label: "Manuals & documents", detail: `${hub.downloads.length} to download`, genieIcon: "files", onClick: () => scrollTo("downloads") });
    if (hub.emailAddress) list.push({ key: "email", label: "Send an email", detail: "Another way to ask", genieIcon: "mail", href: `mailto:${hub.emailAddress}` });
    if (hub.showApp) list.push({ key: "app", label: "Get the app", detail: "Take us with you", genieIcon: "genie-lamp", onClick: () => scrollTo("app") });
    return list;
  }, [hub, scrollTo]);

  return { stage, openWithQuestion, openStage, closeStage, channels };
};
